/**
 * Reine Zeit-/Datums-Hilfen für die Engine — abhängigkeitsfrei, UTC-basiert
 * (keine DST-Effekte). Uhrzeiten als „HH:MM", Datum als „YYYY-MM-DD".
 */

const MS_PRO_TAG = 86_400_000

/** „HH:MM" → Minuten ab Mitternacht; null bei ungültig/leer. */
export function hhmmZuMinuten(s: string | null | undefined): number | null {
  if (!s) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim())
  if (!m) return null
  const std = Number(m[1])
  const min = Number(m[2])
  if (std > 23 || min > 59) return null
  return std * 60 + min
}

/** Dauer Kommt→Geht in Stunden (≥ 0; kein Mitternachts-Übergang). */
export function dauerStunden(
  kommt: string | null | undefined,
  geht: string | null | undefined
): number {
  const k = hhmmZuMinuten(kommt)
  const g = hhmmZuMinuten(geht)
  if (k === null || g === null) return 0
  return Math.max(0, (g - k) / 60)
}

/** Überlappung [a1,a2] ∩ [b1,b2] in Minuten (≥ 0). */
function ueberlapp(a1: number, a2: number, b1: number, b2: number): number {
  return Math.max(0, Math.min(a2, b2) - Math.max(a1, b1))
}

/**
 * Nacht-Stunden innerhalb [kommt,geht]. Das Nachtfenster darf über Mitternacht
 * gehen (Start > Ende ⇒ [0,Ende] ∪ [Start,24:00]).
 */
export function nachtStunden(
  kommt: string | null | undefined,
  geht: string | null | undefined,
  startMin: number,
  endeMin: number
): number {
  const k = hhmmZuMinuten(kommt)
  const g = hhmmZuMinuten(geht)
  if (k === null || g === null || g <= k) return 0
  const min =
    startMin <= endeMin
      ? ueberlapp(k, g, startMin, endeMin)
      : ueberlapp(k, g, 0, endeMin) + ueberlapp(k, g, startMin, 24 * 60)
  return min / 60
}

/** Rundet Stunden auf ein Minuten-Raster (falls aktiv), sonst unverändert. */
export function rundeStunden(std: number, schrittMin: number, aktiv: boolean): number {
  if (!aktiv || schrittMin <= 0) return std
  const schrittStd = schrittMin / 60
  return Math.round(std / schrittStd) * schrittStd
}

/** „YYYY-MM-DD" → UTC-Millisekunden des Tagesbeginns. */
export function parseUtc(iso: string): number {
  const [j, m, t] = iso.split('-').map(Number)
  return Date.UTC(j, (m ?? 1) - 1, t ?? 1)
}

/** Wochentag 1=Mo … 7=So für ein ISO-Datum. */
export function wochentagVon(iso: string): number {
  const tag = new Date(parseUtc(iso)).getUTCDay() // 0=So … 6=Sa
  return ((tag + 6) % 7) + 1
}

/** Montag 00:00 UTC der Kalenderwoche eines Datums (als „YYYY-MM-DD"). */
export function montagKey(iso: string): string {
  const ms = parseUtc(iso)
  const versatz = (new Date(ms).getUTCDay() + 6) % 7
  return new Date(ms - versatz * MS_PRO_TAG).toISOString().slice(0, 10)
}

/** Anzahl Tage im Monat (monat 1..12). */
export function tageImMonat(jahr: number, monat: number): number {
  return new Date(Date.UTC(jahr, monat, 0)).getUTCDate()
}
